import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";
import { getReviews } from "../../services/posthogGeo";
import "../Users/Users.scss";

// Admin "Review Prompts" tab. Two eras of data land here: the custom ZONA
// Alert (2026-07-11 to 09-14, event `review_prompt_shown`, Rate/Not-now
// tracked) and the native iOS prompt either side of it (`review_prompt_
// requested`, delivery only because iOS gives no callback). Both count as
// "prompted" so the window keeps its history; outcome shows only where the
// custom prompt recorded one.
const fmtMiami = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }) + " ET";
  } catch {
    return "—";
  }
};

const pct = (n, total) => (total ? `${Math.round((n / total) * 100)}%` : "—");

const ReviewPrompts = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [names, setNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(false);
    const res = await getReviews(45);
    if (!res) {
      setErr(true);
      setReviews([]);
      setLoading(false);
      return;
    }
    const list = res.reviews || [];
    setReviews(list);
    setLoading(false);
    // Resolve names for the users who were actually shown a prompt.
    const uids = [...new Set(list
        .filter((r) => r.event === "review_prompt_shown" || r.event === "review_prompt_requested")
        .map((r) => r.uid))];
    uids.forEach(async (uid) => {
      try {
        const { data } = await api.get(`/users/${uid}`);
        const u = data?.user;
        if (u) {
          setNames((prev) => ({
            ...prev,
            [uid]: { name: `${u.firstName || ""} ${u.lastName || ""}`.trim(), email: u.email, mongoId: u._id },
          }));
        }
      } catch {
        /* leave as uid */
      }
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shown = reviews.filter(
    (r) => r.event === "review_prompt_shown" || r.event === "review_prompt_requested",
  );
  const rated = reviews.filter((r) => r.event === "review_prompt_rated");
  const dismissed = reviews.filter((r) => r.event === "review_prompt_dismissed");
  const skipped = reviews.filter((r) => r.event === "review_prompt_skipped");
  const unavailable = reviews.filter((r) => r.event === "review_prompt_unavailable");
  const alreadyFired = skipped.filter((r) => r.reason === "already_fired");
  const cooldown = skipped.filter((r) => r.reason === "cooldown");
  const shownUsers = new Set(shown.map((r) => r.uid)).size;
  const skippedUsers = new Set(skipped.map((r) => r.uid)).size;

  // Each trigger fires once per user, so uid+trigger ties an answer back to
  // the prompt that asked it.
  const outcomes = {};
  [...rated, ...dismissed].forEach((r) => {
    outcomes[`${r.uid}|${r.trigger || ""}`] = r.event === "review_prompt_rated" ? "rated" : "dismissed";
  });

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <h1 style={{ margin: 0, color: "#fff" }}>Review Prompts</h1>
            <p style={{ margin: "6px 0 0", color: "rgba(255,255,255,0.55)", fontSize: 13, maxWidth: 820, lineHeight: 1.6 }}>
              The rating prompt, fired once per trigger per user with a 24h cooldown. As of 2026-09-14 this is the
              native iOS prompt again: it rates in one tap without leaving the app, but iOS decides whether to show
              it and gives no callback, so these rows are delivery, not proof anyone saw it. Rows from 2026-07-11 to
              09-14 came from a custom ZONA Alert that deep-linked to the App Store, which is why only those carry an
              outcome. Either way the real scoreboard is the App Store rating count, not this page.
            </p>
          </div>

          {/* Summary */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
            <Stat label="Users asked (45d)" value={loading ? "…" : shownUsers} />
            <Stat label="Prompts fired" value={loading ? "…" : shown.length} />
            <Stat label="Tapped rate (custom era)" value={loading ? "…" : `${rated.length} · ${pct(rated.length, shown.length)}`} accent />
            <Stat label="Not now" value={loading ? "…" : `${dismissed.length} · ${pct(dismissed.length, shown.length)}`} muted />
            <Stat
              label="Skipped by guard"
              value={loading ? "…" : `${skipped.length} · ${skippedUsers} users`}
              sub={loading ? "" : `${alreadyFired.length} already fired · ${cooldown.length} cooldown`}
              muted
            />
            {unavailable.length > 0 && (
              <Stat label="Device can't review" value={unavailable.length} muted />
            )}
          </div>

          <div
            style={{
              width: "100%",
              background: "rgba(255,255,255,0.03)",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.06)",
              overflowX: "auto",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", color: "#fff", minWidth: 820 }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                  <th style={th}>User</th>
                  <th style={th}>Email</th>
                  <th style={th}>Fired at</th>
                  <th style={th}>Trigger</th>
                  <th style={th}>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>
                      Loading…
                    </td>
                  </tr>
                ) : err ? (
                  <tr>
                    <td colSpan={5} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>
                      Could not load review data.
                    </td>
                  </tr>
                ) : shown.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>
                      No prompts fired in this window.
                      {skipped.length > 0 && " Every trigger that came up was skipped by the guard, so nobody new was asked."}
                    </td>
                  </tr>
                ) : (
                  shown.map((r, i) => {
                    const info = names[r.uid];
                    const outcome = outcomes[`${r.uid}|${r.trigger || ""}`];
                    return (
                      <tr
                        key={i}
                        onClick={() => navigate(`/users/${info?.mongoId || r.uid}`)}
                        style={{ cursor: "pointer", borderTop: "1px solid rgba(255,255,255,0.05)" }}
                      >
                        <td style={td}>
                          {info?.name || <span style={{ fontFamily: "monospace", fontSize: 12, opacity: 0.7 }}>{r.uid.slice(0, 12)}…</span>}
                        </td>
                        <td style={{ ...td, color: "rgba(255,255,255,0.7)" }}>{info?.email || "—"}</td>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtMiami(r.created_at)}</td>
                        <td style={td}>
                          <span style={{ background: "rgba(79,48,234,0.2)", color: "#fff", fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 999 }}>
                            {r.trigger || "—"}
                          </span>
                        </td>
                        <td style={td}>
                          <Outcome value={outcome} event={r.event} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </article>
      </div>
    </main>
  );
};

// Native rows can never carry an outcome; a blank custom-era row means the
// prompt was shown and never answered (app closed on it).
const Outcome = ({ value, event }) => {
  const map = {
    rated: { text: "Tapped rate", bg: "rgba(52,199,89,0.18)", color: "#5ddc84" },
    dismissed: { text: "Not now", bg: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.75)" },
  };
  const s = map[value];
  if (!s)
    return (
      <span style={{ fontSize: 12, opacity: 0.4 }}>
        {event === "review_prompt_requested" ? "Not tracked" : "No answer"}
      </span>
    );
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 999 }}>
      {s.text}
    </span>
  );
};

const Stat = ({ label, value, sub, muted, accent }) => (
  <div
    style={{
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.06)",
      borderRadius: 12,
      padding: "14px 18px",
      minWidth: 150,
    }}
  >
    <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}>{label}</div>
    <div style={{ color: accent ? "#5ddc84" : muted ? "rgba(255,255,255,0.7)" : "#fff", fontSize: muted ? 15 : 24, fontWeight: 700, marginTop: 4 }}>
      {value}
    </div>
    {sub ? <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 11, marginTop: 4 }}>{sub}</div> : null}
  </div>
);

const th = {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1.5,
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.6)",
};
const td = { padding: "12px 14px", fontSize: 13, verticalAlign: "middle" };

export default ReviewPrompts;
